<template>
    <div>
        <card>
            <card-body @keyup.native="form.errors.clear()">
                <h5 class="form-title mb-3">
                    {{ form.id ? 'Edit' : 'Create' }} Net Promoter Score
                </h5>
                <div class="row mb-5">
                    <div class="col">
                        <input-text label="Campaign Name" v-model="form.name" :form="form" field="name"></input-text>
                        <div class="form-group">
                            <label>Select locations associated with this campaign</label>
                            <multiselect
                                :options="allLocations"
                                :multiple="true"
                                track-by="id"
                                label="name"
                                :hide-selected="true"
                                v-model="selectedLocations"
                                :close-on-select="false"
                                :class="{'is-invalid': locationsAreInvalid}"
                                placeholder=""
                                @input="checkIfIsSelectAll">
                            </multiselect>
                            <span class="invalid-feedback" v-if="locationsAreInvalid">
                                {{ form.errors.get('location_ids') }}
                            </span>
                        </div>
                    </div>
                    <div class="col">
                        <div v-show="!form.use_location_image">
                            <upload-image image-label="Campaign image" v-model="form.image" :url="campaign.image_url"></upload-image>
                        </div>
                        <div v-if="default_image_on_campaign">
                            <swell-switch
                                margin="mb-0"
                                truthyLabel="On"
                                falsyLabel="Off"
                                switch-label="Use Default Location Image"
                                v-model="form.use_location_image"
                            />
                        </div>
                    </div>
                </div>

                <sms-preference-form
                        class="mb-5"
                        v-model="form.sms_preference"
                        :send-sms="form.send_sms"
                        :location_image_available="default_image_on_campaign"
                        @sendSms="sendSms"
                />

                <email-preference-form
                        class="mb-5"
                        v-model="form.email_preference"
                        :form="form"
                        :send-email="form.send_email"
                        :location_image_available="default_image_on_campaign"
                        @sendEmail="sendEmail"
                />

                <div class="form-group">
                    <swell-switch style="margin-bottom: 1rem;" v-model="form.redirect_to_review_site" switch-label="Direct contact to public review sites after survey (for contacts)"></swell-switch>
                    <transition name="fade-in-left">
                        <div v-if="form.redirect_to_review_site">
                            <div class="form-group">
                                <swell-switch v-model="form.ask_for_feedback" switch-label="Ask for feedback before directing to review sites?"></swell-switch>
                            </div>
                            <div class="form-group">
                                <label>Select Review Sites</label>
                                <span class="invalid-feedback" v-if="reviewSitesAreInvalid">
                                    {{ form.errors.get('review_sites') }}
                                </span>
                                <div class="custom-control custom-checkbox" v-for="site in sites" :key="site">
                                    <input class="form-check-input" type="checkbox"
                                        :id="site" v-model="form.review_sites" :value="site"
                                        :class="{'is-invalid': reviewSitesAreInvalid}" />
                                    <label class="form-check-label" :for="site">
                                        {{ site | capitalize }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
                <swell-switch style="margin-bottom: 1rem;"
                    v-model="form.follow_up"
                    switch-label="Use follow-up messages?">
                </swell-switch>

                <follow-up
                        v-if="form.follow_up"
                        :messages="form.messages"
                        :statuses="statuses"
                        :errors="form.errors"
                        @newMessage="createNewMessage"
                        @deleteMessage="deleteMessage" />

            </card-body>
            <card-footer>
                <div class="float-right">
                    <a class="btn swell-btn btn-secondary" :href="$laroute.route('campaigns.nps.index')">
                        Cancel
                    </a>
                    <a class="swell-btn btn btn-secondary ml-2" v-if="form.id" :href="settingsUrl">
                        Settings
                    </a>
                    <button class="btn swell-btn btn-secondary ml-2" v-if="campaign.id"
                        @click="$refs.preview.open()">
                        Preview
                    </button>
                    <button-loading class="btn swell-btn btn-primary ml-2" :loading="form.submitting"
                        type="button" @click.native="save">
                        Save
                    </button-loading>
                </div>
            </card-footer>
            <campaign-preview ref="preview" :campaign="campaign"></campaign-preview>
        </card>
    </div>
</template>

<script>
    export default {
        props: {
            campaign : {
                default: () => ({})
            },
            locations: {
                type: Array,
                required: true
            },
            sites: {
                type: Array,
                required: true
            }
        },

        data() {
            return {
                form             : new Form(this.setupForm(this.campaign)),
                allLocations     : this.locations,
                selectedLocations: this.campaign.locations || [],
                statuses         : [{name:'sent'},{name:'opened'},{name:'shared'},{name:'failed'}],
                default_image_on_campaign: false
            }
        },

        watch: {
            'form.follow_up'() {
                if (this.form.follow_up && !this.form.id
                    || this.form.follow_up && this.form.id && !this.form.oldMessages.length)
                {
                    this.createNewMessage();
                    return;
                }

                if (!this.form.follow_up && this.form.id) {
                    let messages = this.form.messages.filter((message) => message.id);
                    if (messages.length) {
                        this.form.oldMessages = this.form.messages;
                    }
                }

                if (this.form.follow_up && this.form.oldMessages) {
                    this.form.messages = this.form.oldMessages;
                    this.form.oldMessages = [];
                    return;
                }

                this.form.messages = [];
                this.form.errors.errors = {};
            }
        },

        created() {
            this.setAllToLocations();
            this.checkIfLocationImagesAvailable();
        },

        computed: {
            reviewSitesAreInvalid() {
                return this.form.errors.has('review_sites') && this.form.review_sites.length === 0;
            },

            locationsAreInvalid() {
                return this.form.errors.has('location_ids') && this.form.location_ids.length === 0;
            },

            settingsUrl() {
                return this.$laroute.route('campaigns.settings', { campaign: this.form.id })
            }
        },

        methods: {
            setupForm(data = {}) {
                return {
                    id                     : data.id || null,
                    image                  : data.image || null,
                    file                   : data.file || null,
                    name                   : data.name || null,
                    type                   : 'nps',
                    threshold              : data.threshold || 7,
                    location_ids           : data.location_ids || [],
                    redirect_to_review_site: data.redirect_to_review_site || false,
                    ask_for_feedback       : data.ask_for_feedback || false,
                    email_preference       : data.email_preference || {},
                    sms_preference         : data.sms_preference || {},
                    review_sites           : data.review_sites || [],
                    locations              : data.locations || [],
                    follow_up              : this.campaign.follow_up,
                    messages               : this.campaign.messages || [],
                    oldMessages            : [],
                    send_sms               : this.campaign.id ? this.campaign.send_sms : true,
                    send_email             : this.campaign.id ? this.campaign.send_email : true,
                    use_location_image     : this.campaign.use_location_image || false
                };
            },

            save() {
                let uri    = this.$laroute.route('api.nps-campaigns.store');
                let method = 'post';

                if (this.form.id) {
                    uri    = this.$laroute.route('api.nps-campaigns.update', {nps_campaign: this.form.id});
                    method = 'put';
                }

                this.form.location_ids = this.selectedLocations.map(location => location.id);

                return this.form[method](uri)
                    .then(this.onSaveSuccess.bind(this))
                    .catch(() => this.scrollToFirstError());
            },

            onSaveSuccess() {
                window.location.replace(this.$laroute.route('campaigns.nps.index'));
            },

            setAllToLocations() {
                this.allLocations.unshift({id: 0, name: 'Select All'});
            },

            checkIfIsSelectAll(options) {
                if( options.filter(f => f.id === 0).length > 0 ) {
                    this.selectedLocations = this.locations.filter(f => f.id !== 0);
                }
                this.checkIfLocationImagesAvailable();
            },

            getAllSelectedLocationObjects(){
                let locationArray = [];
                for(let i = 0; i < this.selectedLocations.length; i++){
                    locationArray.push(this.selectedLocations[i].id);
                }
                if(this.allLocations.length){
                    return this.allLocations.filter(location => (locationArray.includes(location.id) && location.id !== 0));
                }
                return [];
            },

            checkIfLocationImagesAvailable () {
                let options = this.getAllSelectedLocationObjects();
                if(options.length){
                    let is_image_available = true;
                    options.forEach(locationOption => {
                        if(!locationOption.image){
                            this.default_image_on_campaign = false;
                            is_image_available = false;
                        }
                        if(is_image_available){
                            this.default_image_on_campaign = true;
                        }
                    });

                }else{
                    this.default_image_on_campaign = false;
                }
            },

            createNewMessage() {
                this.form.messages.push({
                    delay_days: null,
                    subject   : '',
                    content   : '',
                    statuses  : []
                });
            },

            deleteMessage(index) {
                this.$delete(this.form.messages, index);
            },

            sendSms(value) {
                this.form.send_sms = value
            },

            sendEmail(value) {
                this.form.send_email = value
            }
        },
    }
</script>
